import api from '../../lib/api';
import type { FormStructure } from '../surveys/types';

export type QmsRegistryKind = 'non_conformity' | 'claim';

export async function fetchActiveFormVersion(kind: QmsRegistryKind) {
  const { data } = await api.get(`/qms/form-versions/active`, { params: { kind } });
  return data.data as { id: string; version: number; structure: FormStructure };
}

export async function saveFormTemplate(kind: QmsRegistryKind, structure: FormStructure) {
  const { data } = await api.put(`/qms/form-versions`, { kind, structure });
  return data.data;
}

export async function fetchConfigurableLists() {
  const { data } = await api.get('/qms/configurable-lists');
  return data.data as {
    id: string;
    key: string;
    name: string;
    items: { id: string; label: string; sortOrder: number }[];
  }[];
}

export async function addConfigurableListItem(listKey: string, label: string) {
  const { data } = await api.post(`/qms/configurable-lists/${listKey}/items`, { label });
  return data.data;
}

export async function createQmsRecord(payload: Record<string, unknown>) {
  const { data } = await api.post('/qms/records', payload);
  return data.data as { id: string; status: string; message?: string };
}

export async function listQmsRecords(kind: QmsRegistryKind, page = 1, pageSize = 20) {
  const { data } = await api.get('/qms/records', { params: { kind, page, pageSize } });
  return {
    data: data.data as {
      id: string;
      status: string;
      incidentDate: string;
      originType: string;
      attributability: string;
      typificationMode: string;
      createdAt: string;
      reviewOutcome: string;
    }[],
    pagination: data.pagination as { page: number; pageSize: number; total: number; pages: number },
  };
}

export interface QmsStatistics {
  total: number;
  openCount: number;
  closedCount: number;
  byStatus: { status: string; count: number }[];
  byOrigin: { origin: string; count: number }[];
  byAttributability: { attributability: string; count: number }[];
  byTypification: { mode: string; itemId: string | null; itemLabel: string | null; count: number }[];
}

export async function fetchQmsStatistics(kind: QmsRegistryKind) {
  const { data } = await api.get('/qms/records/statistics', { params: { kind } });
  return data.data as QmsStatistics;
}

export interface QmsReportByMonth {
  month: string;
  total: number;
  open: number;
  closed: number;
  external: number;
  internal: number;
}

export interface QmsReportByTypification {
  label: string;
  mode: string;
  total: number;
  external: number;
  internal: number;
}

export interface QmsReports {
  byMonth: QmsReportByMonth[];
  byTypification: QmsReportByTypification[];
  crossOriginAttr: { origin: string; attributability: string; count: number }[];
}

export async function fetchQmsReports(kind: QmsRegistryKind) {
  const { data } = await api.get('/qms/records/reports', { params: { kind } });
  return data.data as QmsReports;
}

export interface QmsStatusSummary {
  contingency: 'none' | 'open' | 'executed' | 'verified';
  correctiveDecision: 'pending' | 'applies' | 'waived' | 'not_required';
  correctiveAction: 'none' | 'in_progress' | 'implemented' | 'verified' | 'closed';
  record: 'open' | 'closed';
}

export async function fetchQmsRecordDetail(id: string) {
  const { data } = await api.get(`/qms/records/${id}`);
  return data.data;
}

export async function postCloseRecord(openingId: string, reason: string) {
  const { data } = await api.post(`/qms/records/${openingId}/close`, { reason });
  return data.data;
}

export async function fetchQmsAssigneeOptions(openingId: string, search = '') {
  const { data } = await api.get<{ success: boolean; data: { users: { id: number; email: string }[] } }>(
    `/qms/records/${openingId}/assignee-options`,
    { params: { search: search.trim() || undefined } }
  );
  return data.data;
}

export async function postReview(id: string, outcome: 'pertinent' | 'dismissed', dismissReason?: string) {
  const { data } = await api.post(`/qms/records/${id}/review`, { outcome, dismissReason });
  return data.data;
}

export async function postContingency(
  openingId: string,
  body: {
    what: string;
    dueAt: string;
    assignedUserId: number;
    resources?: Record<string, unknown> | null;
  }
) {
  const { data } = await api.post(`/qms/records/${openingId}/contingency-actions`, body);
  return data.data;
}

export async function patchContingencyDone(actionId: string, closureNotes: string) {
  const { data } = await api.patch(`/qms/contingency-actions/${actionId}/done`, { closureNotes });
  return data.data;
}

export async function postContingencyVerification(
  openingId: string,
  body: { eliminatedDeviation: boolean; notes?: string }
) {
  const { data } = await api.post(`/qms/records/${openingId}/contingency-verifications`, body);
  return data.data;
}

export async function postCorrectiveDecision(
  openingId: string,
  body: {
    startCorrective: boolean;
    waiveCorrective?: boolean;
    waiveObservation?: string;
    internalTriggersCorrective?: boolean;
  }
) {
  const { data } = await api.post(`/qms/records/${openingId}/corrective-decision`, body);
  return data.data;
}

export async function postRootCauseAnalysis(
  openingId: string,
  body: {
    participantUserIds: number[];
    steps: Array<{
      order: number;
      tool: string;
      inputData?: object | null;
      outputData?: object | null;
      notes?: string;
    }>;
    rootCauses: Array<{ description: string; order?: number }>;
  }
) {
  const { data } = await api.post(`/qms/records/${openingId}/root-cause-analysis`, body);
  return data.data;
}

export async function postCorrectiveAction(
  openingId: string,
  body: {
    what: string;
    dueAt: string;
    assignedUserId: number;
    rootCauseId: string;
    resources?: Record<string, unknown> | null;
  }
) {
  const { data } = await api.post(`/qms/records/${openingId}/corrective-actions`, body);
  return data.data;
}

export async function postFollowUpNote(
  openingId: string,
  body: {
    note: string;
    plannedReviewDate?: string | null;
    contingencyActionId?: string | null;
    correctiveActionId?: string | null;
  }
) {
  const { data } = await api.post(`/qms/records/${openingId}/follow-up-notes`, body);
  return data.data;
}

export async function postTrainingPlanLink(
  actionId: string,
  payload: { externalPlanRef?: string; trainingSessionId?: string },
) {
  const { data } = await api.post(`/qms/corrective-actions/${actionId}/training-plan-links`, payload);
  return data.data;
}

export async function patchCorrectiveImplemented(actionId: string) {
  const { data } = await api.patch(`/qms/corrective-actions/${actionId}/implemented`);
  return data.data;
}

export async function postCorrectiveEfficacy(
  actionId: string,
  efficacyNotes: string,
  efficacyResult: 'eliminated' | 'minimized' | 'failed'
) {
  const { data } = await api.post(`/qms/corrective-actions/${actionId}/efficacy`, {
    efficacyNotes,
    efficacyResult,
  });
  return data.data;
}

export async function postCloseCorrectiveWithoutEfficacy(actionId: string, closureReason: string) {
  const { data } = await api.post(`/qms/corrective-actions/${actionId}/close-without-efficacy`, {
    closureReason,
  });
  return data.data;
}

export async function postExternalCommunication(
  openingId: string,
  body: { channel: 'email' | 'whatsapp' | 'other'; recipient: string; meta?: object }
) {
  const { data } = await api.post(`/qms/records/${openingId}/external-communications`, body);
  return data.data;
}

export async function postChainChild(parentId: string, subtype: 'treatment' | 'corrective_action') {
  const { data } = await api.post(`/qms/records/${parentId}/children`, { subtype });
  return data.data;
}
